import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { format, isValid, parseISO } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import {
  ChevronDown,
  PencilRuler,
  GaugeCircle,
  User,
  BadgeDollarSign,
  Building,
  UserCheck,
  Tag,
  BadgePercent,
  ArrowUpFromDot,
  CircleDot,
  CalendarCheck2,
  RectangleEllipsis,
  ChevronUp,
} from "lucide-react";
import { AskAiButton } from "../../../../chat";
import clsx from "clsx";
import SelectInline from "./../SelectInline";
import AmountInputField from "./../AmountInputField";
import DatePicker from "./../DatePicker";
import AssigneeSelectV2 from "../../../../components/AssigneeSelect";
import LabelsSelect from "../../../../components/LabelsSelect";
import TaskAction from "../../../../actions/TaskAction";
import EditableDetailRow from "./../EditableDetailRow";
import LinkItemPickerV2 from "../../../../main/component/LinkItemPickerV2";
import Avatar from "../../../../components/Avatar";
import CustomField from "../../../../main/CustomField";
import { FileCode } from "lucide-react";
import InputInline from "../../../../components/InputInline";
import DetailPanelSkeleton from "./DetailPanelSkeleton";
import BucketSelect from "../../../../components/BucketSelect";
import ProductsPicker from "../../../../main/component/ProductsPicker";
import JiraTaskIcon from "../../../../JiraTaskIcon";
import { isGmailContext } from "../../../../api/api";
import { formatAmount } from "../../../../api/DataTypeUtils";
import BucketAction from "../../../../actions/BucketAction";
import Card from "../../../../components/ui/Card";
import ItemSelect from "../../../../components/ItemSelect";
import { navigateToItem } from "../../../../Navigator";
import ItemLinkAction from "../../../../actions/ItemLinkAction";

interface DealDetailPanelProps {
  projectId: string;
  contentType: string; // expect "SALE"
}

// Constants
const MAX_VISIBLE_ASSIGNEES = 2;
const DATE_YEAR_MIN = 1990;
const DATE_YEAR_MAX = 2100;

// Helper functions
const toStr = (v: any) => (v === null || v === undefined ? "" : String(v));
const toPlain = (v: any) => (typeof v?.toJS === "function" ? v.toJS() : v);

const normalizeOptions = (opts: any[]): { value: string; label: string }[] => {
  if (!Array.isArray(opts)) return [];
  return opts.map((o: any) => ({
    value: toStr(o?.value),
    label: toStr(o?.label),
  }));
};

const parseAssigneesToIdArray = (v: any): string[] => {
  if (!v) return [];

  const normalizeArray = (arr: any[]): string[] => {
    if (!Array.isArray(arr) || arr.length === 0) return [];
    if (typeof arr[0] === "string") return arr as string[];

    return arr
      .map((it: any) => {
        if (!it) return "";
        if (typeof it === "string") return it;
        if (typeof it?.get === "function") {
          return String(it.get("id") ?? it.get("value") ?? "");
        }
        return String(it.id ?? it.value ?? "");
      })
      .map((s) => String(s).trim())
      .filter(Boolean);
  };

  if (Array.isArray(v)) return normalizeArray(v);

  if (typeof v?.toJS === "function") {
    const x = v.toJS();
    if (Array.isArray(x)) return normalizeArray(x);
    if (typeof x === "string") {
      return x
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  }

  if (typeof v === "string") {
    return v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return [];
};

const filterValidAssigneeIds = (ids: string[]): string[] => {
  return ids.filter((id) => {
    const idStr = String(id).trim();
    if (!idStr) return false;
    if (idStr.startsWith("List [") || idStr.includes("List [")) return false;
    if (idStr.match(/^["'].*["']\s*\]$/)) return false;
    return true;
  });
};

const formatCloseDate = (finish: any, t: (key: string) => string): React.ReactNode => {
  if (!finish) {
    return <span className="text-gray-400">{t("Set a value...")}</span>;
  }

  try {
    let date: Date | undefined;
    const finishType = typeof finish;

    if (finishType === "string") {
      date = /^\d{4}-\d{2}-\d{2}$/.test(finish) ? parseISO(finish) : new Date(finish);
    } else if (finishType === "number") {
      const num = finish as number;
      const ms = num < 1e12 ? num * 1000 : num;
      date = new Date(ms);
    } else {
      date = new Date(finish);
    }

    if (!isValid(date)) {
      return <span className="text-gray-400">{t("Set a value...")}</span>;
    }

    const year = date.getFullYear();
    if (year < DATE_YEAR_MIN || year > DATE_YEAR_MAX) {
      return <span className="text-gray-400">{t("Set a value...")}</span>;
    }

    return format(date, "dd MMM yyyy");
  } catch {
    return <span className="text-gray-400">{t("Set a value...")}</span>;
  }
};

const DealDetail = ({ projectId, contentType, isLoading, task, visibleRowNumber }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const members = useSelector(
    (state: any) => state.projectReducer.getIn(["project", "members"]) || fromJS([])
  );
  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));
  const projectCurrency = useSelector((state: any) =>
    state.projectReducer.getIn(["project", "currency"])
  );
  const currency = projectCurrency || "USD";

  const buckets = useSelector((state: any) => state.bucketReducer.get(contentType)) || fromJS([]);

  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";

  const scoring = useSelector((state: any) => state.scoring);
  const saleStatusImmutable = useSelector((state: any) => state.ruleReducer.getIn(["status"]));
  const saleSourcesImmutable = useSelector((state: any) => state.ruleReducer.getIn(["source"]));
  const saleStagesImmutable = useSelector((state: any) => state.ruleReducer.getIn(["stage"]));
  const saleStatusRaw = useMemo(() => saleStatusImmutable?.toJS?.() ?? [], [saleStatusImmutable]);
  const saleSourcesRaw = useMemo(() => {
    const saleSources = saleSourcesImmutable?.toJS?.() ?? [];
    if (isGmailContext()) {
      saleSources.unshift({ value: "Chrome extension", label: "Chrome extension" });
    }
    return saleSources;
  }, [saleSourcesImmutable]);
  const saleStagesRaw = useMemo(() => saleStagesImmutable?.toJS?.() ?? [], [saleStagesImmutable]);

  // Ensure buckets are loaded
  useEffect(() => {
    if (projectId && contentType && (!buckets || buckets.size === 0)) {
      dispatch(BucketAction.getBuckets(projectId, contentType));
    }
  }, [dispatch, projectId, contentType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Task data helpers
  const data = (task?.get?.("data") as any) || fromJS({});
  const getVal = (fieldId: string) => data.getIn([fieldId, "value"]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getTaskFieldValue used in useMemo below
  const getTaskFieldValue = (fieldId: string) => getVal(fieldId);

  // Normalized options for selects
  const statusOptions = useMemo(() => normalizeOptions(saleStatusRaw), [saleStatusRaw]);
  const stageOptions = useMemo(() => normalizeOptions(saleStagesRaw), [saleStagesRaw]);
  const sourceOptions = useMemo(() => normalizeOptions(saleSourcesRaw), [saleSourcesRaw]);

  // Current field values
  const dealNameRaw = getVal("name");
  const dealName = dealNameRaw || t("Untitled");
  const status = getVal("status") || "OPEN";
  const source = getVal("source") || <span className="text-gray-400">{t("Set a value...")}</span>;
  const stageVal = getVal("stage");
  const finish = getVal("finish");
  const estimatedRevenue = Number(getVal("estimatedRevenue") || 0);
  const productsImmutable = getVal("products");
  const productsPlain: any[] = useMemo(() => toPlain(productsImmutable) || [], [productsImmutable]);

  // Formatted values
  const closeDateText = useMemo(() => formatCloseDate(finish, t), [finish, t]);

  // Computed values
  const predictPercent =
    getVal("estimatedProbability") ||
    scoring?.getIn?.(["estimatedProbability", "estimatedProbability"]) ||
    0;

  const stageLabel = useMemo(() => {
    const found = stageOptions.find((o) => toStr(o.value) === toStr(stageVal));
    if (found?.label) return found.label;
    if (stageVal) return toStr(stageVal);
    return <span className="text-gray-400">{t("Set a value...")}</span>;
  }, [stageOptions, stageVal, t]);

  // Assignee parsing and matching
  const assigneeIds = useMemo(() => {
    const rawAssignees = getVal("assignees");
    const parsedIds = parseAssigneesToIdArray(rawAssignees);
    return filterValidAssigneeIds(parsedIds);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const assigneeMatches = useMemo(() => {
    return assigneeIds.map((id) => {
      const memberByAccount = members.find((m: any) => String(m.get("accountId")) === String(id));
      const memberById =
        memberByAccount || members.find((m: any) => String(m.get("id")) === String(id));
      const memberByValue =
        memberById || members.find((m: any) => String(m.get("value")) === String(id));
      const found = memberByValue || null;

      return {
        accountId: String(id),
        displayName:
          found?.get?.("displayName") ||
          found?.get?.("name") ||
          found?.get?.("email") ||
          String(id),
        email: found?.get?.("email"),
        avatarUrl: found?.get?.("avatarUrl"),
      };
    });
  }, [assigneeIds, members]);

  // Render assignees with avatars
  const renderAssigneeDisplay = useMemo(() => {
    if (assigneeMatches.length === 0) {
      return <span className="text-gray-400">{t("Unassigned")}</span>;
    }

    const visibleAssignees = assigneeMatches.slice(0, MAX_VISIBLE_ASSIGNEES);
    const remainingCount = assigneeMatches.length - MAX_VISIBLE_ASSIGNEES;

    return (
      <div className="flex items-center gap-x-2 overflow-hidden">
        {visibleAssignees.map((user) => (
          <div
            key={`assignee_${user.accountId}`}
            className="flex items-center space-x-1.5 shrink-0"
          >
            <Avatar sizeIcon={4} avatarUrl={user.avatarUrl} displayName={user.displayName} />
            <span className="text-[12px] leading-4 font-medium text-gray-800 whitespace-nowrap">
              {user.displayName}
            </span>
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="flex items-center space-x-1.5 shrink-0">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-[10px] font-medium text-gray-600">+{remainingCount}</span>
            </div>
          </div>
        )}
      </div>
    );
  }, [assigneeMatches, t]);

  // Render Jira issue label with icon and issue key badge
  const renderJiraIssueLabel = () => {
    const v = getVal("issue");
    const plain = toPlain(v);
    let issueData: any = null;

    if (Array.isArray(plain) && plain.length > 0) {
      issueData = plain[0];
    } else if (plain && typeof plain === "object") {
      issueData = plain;
    }

    if (!issueData) {
      return <span className="text-gray-400">{t("No issue")}</span>;
    }

    // Issue key is in 'value' or 'id' field, summary is in 'label' field
    const issueKey = issueData.value ?? issueData.id ?? "";
    const summary = issueData.label ?? "";
    const iconUrl = issueData.iconUrl ?? "";

    // Build Jira browse URL
    const tenantUrl = tenantInfo?.get?.("tenantUrl") || "";
    const jiraUrl = tenantUrl && issueKey ? `${tenantUrl}/browse/${issueKey}` : "";

    const handleOpenJiraIssue = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (jiraUrl) {
        window.open(jiraUrl, "_blank");
      }
    };

    return (
      <div className="flex items-center gap-1 min-w-0 group/issue">
        <div className="w-4 h-4 shrink-0 flex items-center justify-center">
          {iconUrl ? (
            <img src={iconUrl} alt="" className="w-4 h-4 object-contain" />
          ) : (
            <JiraTaskIcon className="w-4 h-4" />
          )}
        </div>
        <div
          className={`inline-flex items-center rounded-full border border-blue-300 px-[3px] py-[1px] shrink-0 ${jiraUrl ? "cursor-pointer hover:bg-blue-50" : ""}`}
          onClick={jiraUrl ? handleOpenJiraIssue : undefined}
          title={jiraUrl ? t("Open in Jira") : ""}
        >
          <span
            className={`text-[10px] font-medium leading-[16px] text-blue-600 px-[2.5px] whitespace-nowrap ${jiraUrl ? "hover:underline" : ""}`}
          >
            {issueKey}
          </span>
        </div>
        {summary && (
          <span className="text-xs font-medium text-gray-900 truncate" title={summary}>
            {summary}
          </span>
        )}
      </div>
    );
  };

  const issueLabel = renderJiraIssueLabel();

  // Handlers
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleUpdateField used in useMemo
  const handleUpdateField = async (fieldId: string, fieldValue: any) => {
    const itemId = task.get("id");
    if (itemId) {
      await dispatch(TaskAction.updateTask(itemId, fieldId, fieldValue));
    }
  };

  // Format custom field display value
  // eslint-disable-next-line react-hooks/exhaustive-deps -- formatCustomFieldValue used in useMemo
  const formatCustomFieldValue = (val: any, fieldType: string): React.ReactNode => {
    if (val === null || val === undefined || val === "") {
      return <span className="text-gray-400">{t("Set a value...")}</span>;
    }
    if (fieldType === "boolean") {
      return val === "yes" || val === true ? t("Yes") : t("No");
    }
    if (fieldType === "datetime") {
      return val ? (
        new Date(val).toLocaleDateString()
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      );
    }
    return String(val);
  };

  // Row builder to unify structure
  // eslint-disable-next-line react-hooks/exhaustive-deps -- buildRow used in useMemo
  const buildRow = (
    key: string,
    icon: React.ReactNode,
    label: string,
    editComponent: React.ReactNode,
    valueContent: React.ReactNode,
    usePopover?: boolean
  ) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <EditableDetailRow label={label} editComponent={editComponent} usePopover={usePopover}>
        {valueContent}
      </EditableDetailRow>
    </div>
  );

  const buildRowLabels = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full py-0.5">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <LabelsSelect
            projectId={projectId}
            contentType={contentType}
            type="INLINE"
            value={toPlain(getVal("labels")) || []}
            onChange={(labelIds: string[]) => handleUpdateField("labels", labelIds)}
            placeholder={t("Select labels")}
          />
        </div>
      </div>
    </div>
  );

  const buildRowBucket = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <BucketSelect
            projectId={projectId}
            contentType={contentType}
            type="INLINE"
            value={getTaskFieldValue("bucketId")}
            onChange={(bucketId: string) => handleUpdateField("bucketId", bucketId)}
            placeholder={t("Select...")}
          />
        </div>
      </div>
    </div>
  );

  const buildRowContact = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <ItemSelect
            projectId={projectId}
            contentType="CONTACT"
            type="INLINE"
            value={getTaskFieldValue("contact")?.toJS()}
            onChange={async (contact: any) => {
              await handleUpdateField("contact", contact);
              dispatch(ItemLinkAction.getItemLink(task.get("id")));
            }}
            placeholder={t("No contact")}
            isClearable
            onOpenItem={(contact: any) => {
              const itemData = fromJS({
                id: contact.value || contact.id,
                type: "CONTACT",
                projectId: projectId,
              });
              navigateToItem({ item: itemData, navigate });
            }}
          />
        </div>
      </div>
    </div>
  );

  const buildRowAccount = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <ItemSelect
            projectId={projectId}
            contentType="COMPANY"
            type="INLINE"
            value={getTaskFieldValue("account")?.toJS()}
            onChange={async (account: any) => {
              await handleUpdateField("account", account);
              dispatch(ItemLinkAction.getItemLink(task.get("id")));
            }}
            placeholder={t("No account")}
            isClearable
            onOpenItem={(account: any) => {
              const itemData = fromJS({
                id: account.value || account.id,
                type: "COMPANY",
                projectId: projectId,
              });
              navigateToItem({ item: itemData, navigate });
            }}
          />
        </div>
      </div>
    </div>
  );

  const buildRowEstimatedRevenue = (key: string, icon: React.ReactNode, label: string) => {
    return (
      <div key={key} className="flex items-center w-full">
        <div className="flex items-center">{icon}</div>
        <EditableDetailRow
          label={label}
          editComponent={
            <AmountInputField
              value={estimatedRevenue}
              variant="inline"
              onFinish={(val: string) => handleUpdateField("estimatedRevenue", val)}
              disabled={productsPlain.length > 0}
            />
          }
          usePopover={false}
          rightComponent={
            <ProductsPicker
              projectId={projectId}
              currency={currency}
              panelAnchor="bottom start"
              renderTarget={(isOpen, setIsOpen, hasProducts) => {
                return (
                  <div
                    className="text-xs text-blue-600 cursor-pointer"
                    onClick={() => setIsOpen(true)}
                  >
                    {hasProducts ? t("Edit products") : t("Add products")}
                  </div>
                );
              }}
              value={productsPlain}
              onChange={async (products, totalAmount) => {
                await handleUpdateField("estimatedRevenue", totalAmount);
                await handleUpdateField("products", products);
              }}
            />
          }
        >
          {formatAmount(i18n.language, estimatedRevenue, currency, "0$")}
        </EditableDetailRow>
      </div>
    );
  };

  // Render custom fields
  const renderCustomFields = useMemo(() => {
    if (!fields) return [];

    const customFields = fields.filter(
      (field: any) => field.get("isCustomField") && field.get("formType") === contentType
    );

    if (!customFields || customFields.size === 0) return [];

    return customFields
      .map((customField: any) => {
        const fieldId = customField.get("id");
        const fieldType = customField.get("type");
        const fieldData = {
          ...customField.toJS(),
          value: getTaskFieldValue(fieldId),
        };

        return buildRow(
          `custom_${fieldId}`,
          <FileCode className="w-4 h-4 text-gray-500" />,
          t(customField.get("label") || fieldId),
          <div className="w-full">
            <CustomField
              field={fieldData}
              variant="inline"
              onChange={(value: any) => handleUpdateField(fieldId, value)}
            />
          </div>,
          formatCustomFieldValue(getTaskFieldValue(fieldId), fieldType)
        );
      })
      .toArray();
  }, [
    fields,
    contentType,
    getTaskFieldValue,
    t,
    buildRow,
    formatCustomFieldValue,
    handleUpdateField,
  ]);

  const rows: React.ReactNode[] = [
    buildRow(
      "dealName",
      <PencilRuler className="w-4 h-4 text-gray-500" />,
      t("Deal Name"),
      <InputInline
        value={dealNameRaw || ""}
        onFinish={(val: string) => {
          if (val && val.trim()) {
            handleUpdateField("name", val);
          }
        }}
        variant="inline"
      />,
      dealName
    ),
    ...(isJira
      ? [
          buildRow(
            "jiraIssue",
            <JiraTaskIcon className="w-4 h-4" />,
            t("Jira Issue"),
            <LinkItemPickerV2
              type="TASK"
              projectId={String(projectId || "")}
              items={getVal("issue")}
              autoOpen
              maxWidth="100%"
              showTriggerHeight={true}
              customTrigger={issueLabel}
              onChange={(newIssue) => {
                handleUpdateField("issue", newIssue);
              }}
            />,
            issueLabel
          ),
        ]
      : []),
    buildRowBucket(
      "bucketId",
      <GaugeCircle className="w-4 h-4 text-gray-500" />,
      t("Sales Funnel")
    ),
    buildRow(
      "assignee",
      <User className="w-4 h-4 text-gray-500" />,
      t("Assignee"),
      <AssigneeSelectV2
        value={getVal("assignees")}
        autoOpen
        className="z-50"
        showTriggerHeight={true}
        multiple={true}
        customTrigger={renderAssigneeDisplay}
        onChange={(val) => {
          const ids = Array.isArray(val)
            ? val
            : String(val || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
          handleUpdateField("assignees", ids);
        }}
      />,
      renderAssigneeDisplay
    ),
    buildRowEstimatedRevenue(
      "estimatedRevenue",
      <BadgeDollarSign className="w-4 h-4 text-gray-500" />,
      t("Estimated Revenue")
    ),
    buildRowContact(
      "contact",
      <UserCheck className="w-4 h-4 text-gray-500" />,
      t("Primary Contact")
    ),
    buildRowAccount(
      "account",
      <Building className="w-4 h-4 text-gray-500" />,
      t("Primary Account")
    ),
    buildRowLabels("labels", <Tag className="w-4 h-4 text-gray-500" />, t("Labels")),
    buildRow(
      "estimatedProb",
      <BadgePercent className="w-4 h-4 text-gray-500" />,
      t("Estimated Probability"),
      <AmountInputField
        value={predictPercent}
        isPercentage
        variant="inline"
        onFinish={(val: string) => handleUpdateField("estimatedProbability", val)}
      />,
      `${Math.round(predictPercent)}%`
    ),
    buildRow(
      "status",
      <ArrowUpFromDot className="w-4 h-4 text-gray-500" />,
      t("Status"),
      <SelectInline
        autoOpen
        customTrigger={status}
        options={statusOptions}
        value={toStr(status)}
        onChange={(val: string) => handleUpdateField("status", val)}
      />,
      status
    ),
    buildRow(
      "stage",
      <CircleDot className="w-4 h-4 text-gray-500" />,
      t("Stage"),
      <SelectInline
        autoOpen
        customTrigger={stageLabel}
        options={stageOptions}
        value={toStr(stageVal ?? "")}
        onChange={(val: string) => handleUpdateField("stage", val)}
      />,
      stageLabel
    ),
    buildRow(
      "closeDate",
      <CalendarCheck2 className="w-4 h-4 text-gray-500" />,
      t("Close Date"),
      <DatePicker
        selected={finish}
        variant="inline"
        onChange={(val: string) => handleUpdateField("finish", val)}
        autoOpen
        customTrigger={closeDateText}
        showTriggerHeight={true}
      />,
      closeDateText
    ),
    buildRow(
      "source",
      <RectangleEllipsis className="w-4 h-4 text-gray-500" />,
      t("Source"),
      <SelectInline
        autoOpen
        customTrigger={source}
        options={sourceOptions}
        value={toStr(source)}
        onChange={(val: string) => handleUpdateField("source", val)}
      />,
      source
    ),
    // Custom Fields
    ...renderCustomFields,
  ];

  if (!task.get("id")) {
    return (
      <div className="px-3">
        <div className="text-xs text-gray-500 py-4 text-center">{t("No information")}</div>
      </div>
    );
  }

  const visibleRows = visibleRowNumber === -1 ? rows : rows.slice(0, visibleRowNumber);

  return (
    <div className="px-3">
      {isLoading ? (
        <DetailPanelSkeleton rows={visibleRows.length} />
      ) : (
        visibleRows.map((row, idx) => <React.Fragment key={idx}>{row}</React.Fragment>)
      )}
    </div>
  );
};

const DealDetailPanel: React.FC<DealDetailPanelProps> = ({ projectId, contentType }) => {
  const { t } = useTranslation();

  const isLoadingTask = useSelector((state: any) => state.taskReducer.get("pending"));
  const task =
    useSelector((state: any) => state.taskReducer.getIn(["task", contentType])) || fromJS({});

  const [collapsed, setCollapsed] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(true);

  const LeftIcon = useMemo(() => {
    return (
      <ChevronDown
        className={clsx(
          "w-4 h-4 transition-transform shrink-0",
          collapsed ? "-rotate-90" : "rotate-0"
        )}
      />
    );
  }, [collapsed]);

  if (contentType !== "SALE") return null;

  const recordId = String(task.get("id") || "");
  const dealDisplayName =
    (task.getIn(["data", "name", "value"]) as string | undefined) ||
    (t("Untitled") as string);
  const dealStage = (task.getIn(["data", "stage", "value"]) as string | undefined) || "";
  const dealIconUrl = (task.get("iconUrl") as string | undefined) || undefined;

  return (
    <Card
      bgClass={"bg-white"}
      borderClass={"border border-gray-200"}
      roundedClass={"rounded-[12px]"}
      shadow={"custom"}
      shadowClass={"shadow-[0_1px_1px_0_rgba(0,0,0,0.05)]"}
    >
      <div className="px-3 py-2 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-end gap-1 text-sm leading-5 font-medium text-gray-800 hover:text-gray-700"
        >
          {LeftIcon}
          {<span className="truncate">{t("Deal Detail")}</span>}
        </button>
        <AskAiButton
          workspaceId={projectId}
          recordId={recordId}
          recordType="SALE"
          displayName={dealDisplayName}
          subtitle={dealStage || (t("Deal") as string)}
          avatarUrl={dealIconUrl}
          ariaLabel={t("Ask AI about this deal") as string}
        />
      </div>
      {!collapsed && (
        <>
          <DealDetail
            projectId={projectId}
            contentType={contentType}
            isLoading={isLoadingTask}
            task={task}
            visibleRowNumber={internalCollapsed ? 6 : -1}
          />
          <div className="px-3 pb-3 mt-2">
            <button
              type="button"
              onClick={() => setInternalCollapsed((v) => !v)}
              className="inline-flex items-center gap-1 text-[10px] leading-4 text-gray-500 hover:text-gray-700"
            >
              <span>{internalCollapsed ? t("Expand") : t("Collapse")}</span>
              {internalCollapsed ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronUp className="w-3 h-3" />
              )}
            </button>
          </div>
        </>
      )}
    </Card>
  );
};

export default DealDetailPanel;
